import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { ReportWithWorksheet } from "@/components/ReportWithWorksheet";
import { FlowerHeatmap } from "@/components/FlowerHeatmap";
import { AreaTimeline } from "@/components/AreaTimeline";
import { api, errMsg } from "@/lib/api";
import { AREAS, band, fmtDate, fmtScore, total } from "@/lib/heat";

const Delta = ({ v }) => {
  const n = Math.round(v * 10) / 10;
  const cls = n > 0 ? "text-emerald-600" : n < 0 ? "text-red-600" : "text-slate-500";
  return <span className={`font-mono text-xs font-semibold ${cls}`}>{n > 0 ? `+${fmtScore(n)}` : fmtScore(n)}</span>;
};

const BeforeAfter = ({ first, latest }) => (
  <section className="avoid-break rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm print-plain" data-testid="before-after">
    <h2 className="text-2xl font-bold tracking-tight text-slate-900">Before &amp; after</h2>
    <div className="mt-6 grid items-center gap-6 md:grid-cols-[1fr_auto_1fr]">
      {[first, latest].map((e, i) => (
        <div key={e.id} className={`text-center ${i === 1 ? "md:order-3" : ""}`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{i === 0 ? "First result" : "Most recent"}</div>
          <div className="font-display font-semibold text-slate-900">{fmtDate(e.submitted_at)}</div>
          <div className="mt-2 flex justify-center"><FlowerHeatmap scores={e.scores} size={260} testId={`before-after-heatmap-${i}`} /></div>
        </div>
      ))}
      <ArrowRight className="mx-auto hidden h-8 w-8 text-slate-300 md:block md:order-2" />
    </div>
    <div className="mt-6 grid grid-cols-2 sm:grid-cols-6 gap-3">
      {AREAS.map((a) => (
        <div key={a.key} className="rounded-xl bg-slate-50 p-3" data-testid={`change-${a.key}`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{a.label}</div>
          <div className="mt-1 font-mono text-sm text-slate-800">{fmtScore(first.scores[a.key])} → {fmtScore(latest.scores[a.key])}</div>
          <Delta v={latest.scores[a.key] - first.scores[a.key]} />
          <div className="text-[11px]" style={{ color: band(latest.scores[a.key]).hex }}>{band(latest.scores[a.key]).label}</div>
        </div>
      ))}
      <div className="rounded-xl bg-slate-50 p-3" data-testid="change-total">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Total</div>
        <div className="mt-1 font-mono text-sm text-slate-800">{fmtScore(total(first.scores))} → {fmtScore(total(latest.scores))}</div>
        <Delta v={total(latest.scores) - total(first.scores)} />
      </div>
    </div>
  </section>
);

const PastCard = ({ e, latest, linked }) => {
  const Wrap = linked ? Link : "div";
  return (
  <Wrap {...(linked ? { to: `/reports/individual/${e.id}` } : {})} data-testid={`past-result-${e.id}`} className={`avoid-break block rounded-xl border border-slate-200 bg-white p-3 ${linked ? "hover:border-[#1F4266]/40 transition-colors" : ""}`}>
    <div className="flex justify-center"><FlowerHeatmap scores={e.scores} size={140} labels={false} testId={`past-heatmap-${e.id}`} /></div>
    <div className="mt-2 flex items-baseline justify-between">
      <span className="font-display font-semibold text-slate-900">{fmtDate(e.submitted_at)}</span>
      {latest && <span className="text-[10px] font-mono uppercase text-[#1F4266]">Latest</span>}
    </div>
    <div className="mt-2 grid grid-cols-5 gap-1 text-center">
      {AREAS.map((a) => (
        <div key={a.key}>
          <div className="text-[9px] uppercase tracking-wide text-slate-400">{a.label.slice(0, 3)}</div>
          <div className="font-mono text-xs text-slate-800">{fmtScore(e.scores[a.key])}</div>
        </div>
      ))}
    </div>
    <div className="mt-1.5 text-right font-mono text-xs font-semibold text-[#1F4266]">Total {fmtScore(total(e.scores))}</div>
  </Wrap>
  );
};

export const ProgressSections = ({ entries, linked = false, who = "this person" }) => {
  const latest = entries[0];
  const chrono = [...entries].reverse();
  return (
    <>
      {entries.length > 1 && (
        <>
          <div className="page-break" />
          <BeforeAfter first={chrono[0]} latest={latest} />
          <section className="avoid-break rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm print-plain">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">Progress over time</h2>
            <p className="mt-1 mb-4 text-sm text-slate-500">Each area's score at every assessment (0–36).</p>
            <AreaTimeline points={chrono.map((e) => ({ label: fmtDate(e.submitted_at), scores: e.scores }))} testId="person-timeline" />
          </section>
        </>
      )}
      <section className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm print-plain" data-testid="past-results">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">All results</h2>
        <p className="mt-1 text-sm text-slate-500">
          {entries.length} result{entries.length === 1 ? "" : "s"} on record, newest first.
          {entries.length === 1 && ` A before & after view appears once ${who} retake${who === "you" ? "" : "s"} the assessment.`}
        </p>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {entries.map((e, i) => <PastCard key={e.id} e={e} latest={i === 0} linked={linked} />)}
        </div>
      </section>
    </>
  );
};

export default function PersonReport() {
  const { personKey } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/people/${encodeURIComponent(personKey)}`).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  }, [personKey]);

  if (error) return <p data-testid="person-error" className="text-red-600">{error}</p>;
  if (!data) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#1F4266]" />;

  const entries = data.entries;

  return (
    <div className="space-y-8" data-testid="person-report">
      <Link to="/entries" className="no-print inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#1F4266]" data-testid="back-to-entries">
        <ArrowLeft className="h-4 w-4" /> Back to entries
      </Link>
      <ReportWithWorksheet data={entries[0]}>
        <ProgressSections entries={entries} linked />
      </ReportWithWorksheet>
    </div>
  );
}
